import { useEffect, useRef, useState } from "react";

import type { OPSUserRoleVO } from "@/api/accounts/type";
import { listRolesByIds } from "@/api/system/roles";
import type { RoleBatchListItemVO } from "@/api/system/roles/type";

/**
 * 按选中身份批量拉取角色详情
 * @param selectedEntityRole 当前选中的身份分组，会员身份传 null 跳过请求
 * @param reloadKey 递增后强制重新拉取（保存权限后刷新）
 */
export const useEntityRoleBatch = (selectedEntityRole: OPSUserRoleVO | null, reloadKey = 0) => {
  // 批量查询到的角色列表
  const [roles, setRoles] = useState<RoleBatchListItemVO[]>([]);
  // 角色列表加载中
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!selectedEntityRole) {
      requestIdRef.current += 1;
      setRoles([]);
      setLoading(false);
      return;
    }
    // 从身份分组提取各端 roleId
    const roleIds = (selectedEntityRole.userRoleInfoList ?? [])
      .map((item) => item.roleId)
      .filter(Boolean);
    if (roleIds.length === 0) {
      requestIdRef.current += 1;
      setRoles([]);
      setLoading(false);
      return;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    /** 批量查询角色详情 */
    const loadRoles = async () => {
      try {
        const res = await listRolesByIds({ roleIds });
        if (requestId !== requestIdRef.current) return;
        setRoles(res.data ?? []);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setRoles([]);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };
    void loadRoles();
  }, [selectedEntityRole, reloadKey]);

  return { roles, loading };
};
